import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Radio, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { publicApi } from "@/lib/api";
import { NavItem, Page } from "@/types";
import { isNavPathActive, renderNavIcon } from "@/components/nav/navIcons";

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyThemeColors(primary?: string, secondary?: string, accent?: string) {
  const root = document.documentElement;
  if (primary && /^#[0-9a-fA-F]{6}$/.test(primary)) {
    root.style.setProperty("--primary", hexToHsl(primary));
    root.style.setProperty("--ring", hexToHsl(primary));
  }
  if (secondary && /^#[0-9a-fA-F]{6}$/.test(secondary)) {
    root.style.setProperty("--secondary", hexToHsl(secondary));
    root.style.setProperty("--foreground", hexToHsl(secondary));
  }
  if (accent && /^#[0-9a-fA-F]{6}$/.test(accent)) {
    root.style.setProperty("--accent", hexToHsl(accent));
  }
}

function applyFavicon(faviconUrl: string) {
  const selectors = [
    { rel: "icon", type: "image/png", sizes: "32x32" },
    { rel: "icon", type: "image/png", sizes: "16x16" },
    { rel: "apple-touch-icon", sizes: "180x180" },
  ];
  selectors.forEach(({ rel, type, sizes }) => {
    const existing = document.querySelector<HTMLLinkElement>(
      `link[rel="${rel}"]${sizes ? `[sizes="${sizes}"]` : ""}`
    );
    if (existing) {
      existing.href = faviconUrl;
      if (type) existing.type = "image/png";
    } else {
      const link = document.createElement("link");
      link.rel = rel;
      if (type) link.type = type;
      if (sizes) link.setAttribute("sizes", sizes);
      link.href = faviconUrl;
      document.head.appendChild(link);
    }
  });
  // Also replace SVG icon
  const svgIcon = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]');
  if (svgIcon) svgIcon.href = faviconUrl;
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [pageNavItems, setPageNavItems] = useState<NavItem[]>([]);
  const [businessDirectoryPublic, setBusinessDirectoryPublic] = useState(false);
  const [logoCircleUrl, setLogoCircleUrl] = useState<string | null>(null);
  const [siteName, setSiteName] = useState("Sunny 106.5");
  const [logoFontFamily, setLogoFontFamily] = useState("Inter");
  const location = useLocation();

  useEffect(() => {
    const loadNav = async () => {
      try {
        const [navRes, pagesRes, settingsRes] = await Promise.allSettled([
          publicApi.nav.getAll(),
          publicApi.pages.getNav(),
          publicApi.settings.get(),
        ]);

        if (navRes.status === "fulfilled") {
          setNavItems(navRes.value.data);
        }

        if (pagesRes.status === "fulfilled") {
          const pages: Page[] = pagesRes.value.data;
          const mapped: NavItem[] = pages.map((p) => ({
            id: p.id,
            label: p.title,
            url: `/${p.slug}`,
            displayOrder: p.navOrder ?? 0,
            icon: null,
            children: [],
          }));
          setPageNavItems(mapped);
        }

        if (settingsRes.status === "fulfilled") {
          const s = settingsRes.value.data;
          setBusinessDirectoryPublic(s.businessDirectoryPublic === true);
          setLogoCircleUrl(s.logoCircleUrl || s.logoUrl || "/images/sunny/logo.png");
          setSiteName(s.siteName || "Sunny 106.5");
          setLogoFontFamily(s.logoFontFamily || "Inter");

          if (s.logoFontCssUrl && typeof document !== "undefined") {
            const existing = document.getElementById("logo-font-css") as HTMLLinkElement | null;
            if (!existing) {
              const link = document.createElement("link");
              link.id = "logo-font-css";
              link.rel = "stylesheet";
              link.href = s.logoFontCssUrl;
              document.head.appendChild(link);
            }
          }

          applyThemeColors(s.primaryColor, s.secondaryColor, s.accentColor);

          if (s.faviconUrl) {
            applyFavicon(s.faviconUrl);
          }
        }
      } catch (error) {
        console.error("Failed to load navigation:", error);
      }
    };
    loadNav();
  }, []);

  const coreNav: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { id: -1, label: "Home", url: "/", icon: "home", displayOrder: -300, children: [] },
      { id: -6, label: "Contests", url: "/contests", icon: "gift", displayOrder: -250, children: [] },
      { id: -2, label: "Events", url: "/events", icon: "calendar", displayOrder: -200, children: [] },
      { id: -7, label: "Playlist", url: "/playlist", icon: "music", displayOrder: -150, children: [] },
      { id: -3, label: "News", url: "/blog", icon: "newspaper", displayOrder: -100, children: [] },
      // Photo galleries are intentionally hidden for Sunny for now. Keep admin/gallery code available to turn back on later.
    ];
    if (businessDirectoryPublic) {
      items.push({
        id: -5,
        label: "Directory",
        url: "/directory",
        icon: "store",
        displayOrder: -90,
        children: [],
      });
    }
    return items;
  }, [businessDirectoryPublic]);

  const mergedByUrl = new Map<string, NavItem>();
  [...coreNav, ...navItems, ...pageNavItems].forEach((item) => {
    const urlKey = (item.url || "").trim().toLowerCase();
    if (!urlKey || urlKey === "/fcc-public-file" || mergedByUrl.has(urlKey)) return;
    mergedByUrl.set(urlKey, item);
  });

  const displayNav = Array.from(mergedByUrl.values()).sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  return (
    <header className="bg-white border-b border-slate-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden">
              {logoCircleUrl ? (
                <img
                  src={logoCircleUrl}
                  alt={`${siteName} logo`}
                  className="w-full h-full object-contain"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement?.classList.add("logo-fallback");
                  }}
                />
              ) : (
                <Radio className="w-5 h-5 text-white" />
              )}
            </div>
            <span
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: logoFontFamily }}
            >
              {siteName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {displayNav.map((item) => (
              <Link
                key={item.id}
                to={item.url}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                  isNavPathActive(location.pathname, item.url)
                    ? "bg-primary/10 text-primary"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                {renderNavIcon(item.icon)}
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-slate-200">
            {displayNav.map((item) => (
              <Link
                key={item.id}
                to={item.url}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isNavPathActive(location.pathname, item.url)
                    ? "bg-primary/10 text-primary"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {renderNavIcon(item.icon)}
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
